/** One selectable histogram bucket width. `value` is the API's dataPointsDifference. */
export type ResolutionOption = { label: string; value: string };

/**
 * Bucket widths worth offering for a window of the given length.
 *
 * The list is deliberately narrow at both ends: finer than this and a long
 * window returns tens of thousands of points the chart cannot draw, coarser and
 * a short window collapses to one or two. Options that do not suit the selected
 * period are simply absent rather than shown disabled.
 * @param start - Window start, epoch millis.
 * @param end - Window end, epoch millis.
 * @returns Options from finest to coarsest; the first is the default.
 */
export function getResolutionOptions(start: number, end: number): ResolutionOption[] {
  const diffHours = (end - start) / (1000 * 60 * 60);

  if (diffHours <= 1) {
    return [
      { label: '1 Minute', value: '1m' },
      { label: '5 Minutes', value: '5m' },
      { label: '15 Minutes', value: '15m' }
    ];
  }
  if (diffHours <= 12) {
    return [
      { label: '5 Minutes', value: '5m' },
      { label: '15 Minutes', value: '15m' },
      { label: '30 Minutes', value: '30m' },
      { label: '1 Hour', value: '1h' }
    ];
  }
  if (diffHours <= 24 * 3) {
    return [
      { label: '15 Minutes', value: '15m' },
      { label: '30 Minutes', value: '30m' },
      { label: '1 Hour', value: '1h' },
      { label: '2 Hours', value: '2h' },
      { label: '6 Hours', value: '6h' }
    ];
  }
  if (diffHours <= 24 * 7) {
    return [
      { label: '1 Hour', value: '1h' },
      { label: '2 Hours', value: '2h' },
      { label: '6 Hours', value: '6h' },
      { label: '12 Hours', value: '12h' },
      { label: '1 Day', value: '1d' }
    ];
  }
  if (diffHours <= 24 * 30) {
    return [
      { label: '6 Hours', value: '6h' },
      { label: '12 Hours', value: '12h' },
      { label: '1 Day', value: '1d' },
      { label: '1 Week', value: '1w' }
    ];
  }
  return [
    { label: '1 Day', value: '1d' },
    { label: '1 Week', value: '1w' },
    { label: '1 Month', value: '1M' }
  ];
}

/**
 * Keep a chosen resolution across a change of time period, falling back to the
 * finest option the new period allows. Without this a selection like "1 Minute"
 * would survive a switch to "Last 1 month" and ask the server for ~43k buckets.
 * @param current - Currently selected resolution, if any.
 * @param start - Window start, epoch millis.
 * @param end - Window end, epoch millis.
 * @returns A resolution valid for this window.
 */
export function clampResolution(current: string | null | undefined, start: number, end: number): string {
  const options = getResolutionOptions(start, end);
  return options.some((o) => o.value === current) ? (current as string) : options[0].value;
}

/**
 * Label for a resolution value, for use in a dropdown placeholder.
 * @param value - Resolution value such as "15m".
 * @param start - Window start, epoch millis.
 * @param end - Window end, epoch millis.
 * @returns The matching label, or the raw value when it is not on offer.
 */
export function resolutionLabel(value: string, start: number, end: number): string {
  return getResolutionOptions(start, end).find((o) => o.value === value)?.label ?? value;
}
